import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Seo from '../components/Seo';
import CourseIcon from '../components/CourseIcon';
import CourseCover from '../components/CourseCover';
import { useAuth } from '../context/AuthContext';
import { listCourses } from '../services/courses';
import { listTools } from '../services/tools';
import { createPaymentOrder, verifyPayment } from '../services/payments';
import { createToolPaymentOrder, verifyToolPayment } from '../services/papertrading';
import logo from '../assets/logo-main.png';
import { SunIcon as DbSunIcon, MoonIcon as DbMoonIcon } from '../components/icons';
import './UserDashboard.css';

// ── Razorpay ─────────────────────────────────────────────────────────────────
function loadRazorpayScript() {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) { resolve(); return; }
    const existing = document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]');
    if (existing) { existing.addEventListener('load', resolve); existing.addEventListener('error', reject); return; }
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.async = true;
    s.onload = resolve;
    s.onerror = () => reject(new Error('Failed to load Razorpay'));
    document.body.appendChild(s);
  });
}

/**
 * Tool artwork, keyed by the backend's `tool_id`.
 *
 * Only the icons are local — the API has no line-art, just an emoji that does
 * not match this layout. Name, description, price and coming-soon status all
 * come from the API, so an admin edit is reflected here.
 *
 * Previously this file carried a hardcoded array with `status: 'live'` baked in,
 * which is why SIP and Risk-Reward offered "Open Tool" while the backend had
 * them marked coming-soon.
 */
const TOOL_ICONS = {
  sipcalculator: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="2" width="16" height="20" rx="2"/><line x1="8" y1="6" x2="16" y2="6"/>
      <line x1="8" y1="10" x2="8" y2="10"/><line x1="12" y1="10" x2="12" y2="10"/><line x1="16" y1="10" x2="16" y2="10"/>
      <line x1="8" y1="14" x2="8" y2="14"/><line x1="12" y1="14" x2="12" y2="14"/><line x1="16" y1="14" x2="16" y2="14"/>
      <line x1="8" y1="18" x2="16" y2="18"/>
    </svg>
  ),
  riskrewardcalculator: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
    </svg>
  ),
  papertrading: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/>
    </svg>
  ),
};

const FALLBACK_TOOL_ICON = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9"/><path d="M12 8v4l3 2"/>
  </svg>
);

/** Routes tools open at, once accessible. */
const TOOL_ROUTES = { papertrading: '/tools/papertrading' };

function toolPriceLabel(tool) {
  if (!tool || !tool.is_paid) return 'Free';
  const amount = Number(tool.price);
  if (!Number.isFinite(amount)) return null;
  return `\u20b9${amount.toLocaleString('en-IN')}`;
}

// ── Profile Modal ─────────────────────────────────────────────────────────────
function ProfileModal({ onClose }) {
  const { user, updateProfile } = useAuth();
  const [form, setForm] = useState({ name: user?.name || '', phone: user?.phone || '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    setError(''); setSuccess('');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setError(''); setSuccess('');
    try {
      await updateProfile({ name: form.name.trim(), phone: form.phone.trim() });
      setSuccess('Profile updated successfully.');
    } catch (err) {
      setError(err.response?.data?.message || 'Could not save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="db-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="db-modal" role="dialog" aria-modal="true" aria-labelledby="profile-title">
        <div className="db-modal-header">
          <h2 id="profile-title">My Profile</h2>
          <button className="db-modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="db-modal-avatar-row">
          <span className="db-avatar-lg">
            {(user?.name?.[0] || user?.email?.[0] || 'U').toUpperCase()}
          </span>
          <div>
            <div className="db-modal-name">{user?.name}</div>
            <div className="db-modal-role">
              {user?.role === 'premium' ? 'Premium Member' : 'Member'}
            </div>
          </div>
        </div>

        <form className="db-profile-form" onSubmit={handleSave}>
          <div className="db-field">
            <label htmlFor="pf-email">Email Address</label>
            <input id="pf-email" type="email" value={user?.email || ''} readOnly className="db-input db-input-readonly" />
            <span className="db-field-hint">Email cannot be changed.</span>
          </div>
          <div className="db-field">
            <label htmlFor="pf-name">Full Name</label>
            <input id="pf-name" type="text" name="name" value={form.name} onChange={handleChange}
              className="db-input" required minLength={2} placeholder="Your full name" />
          </div>
          <div className="db-field">
            <label htmlFor="pf-phone">Mobile Number</label>
            <input id="pf-phone" type="tel" name="phone" value={form.phone} onChange={handleChange}
              className="db-input" placeholder="Your mobile number" />
          </div>
          {error   && <div className="db-form-error">{error}</div>}
          {success && <div className="db-form-success">{success}</div>}
          <div className="db-profile-actions">
            <button type="button" className="db-btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="db-btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Dashboard Tab (formerly My Courses) ───────────────────────────────────────
function DashboardTab({ courses, subscribedIds, subscribedTools = [], onBrowse, onBrowseTools }) {
  const navigate = useNavigate();
  const enrolled = courses.filter((c) => subscribedIds.has(c.course_id));
  const openKey = (path) => (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(path); } };

  return (
    <div style={{ paddingTop: '20px' }}>
      <h2 style={{ fontFamily: 'var(--fd)', fontSize: '24px', fontWeight: '700', color: 'var(--text)', marginBottom: '24px' }}>
        Subscribed Courses
      </h2>

      {enrolled.length === 0 ? (
        <div className="db-empty">
          <div className="db-empty-icon">📚</div>
          <h3>No courses yet</h3>
          <p>Browse our catalog and subscribe to a course to get started.</p>
          <button className="db-btn-primary" onClick={onBrowse}>Browse Courses</button>
        </div>
      ) : (
        <div className="course-grid">
          {enrolled.map((c) => (
            <div
              key={c.course_id}
              className="cc"
              style={{ cursor: 'pointer', borderColor: 'var(--accent)' }}
              role="link" tabIndex={0}
              onClick={() => navigate(`/courses/${c.course_id}`)}
              onKeyDown={openKey(`/courses/${c.course_id}`)}
              title={`Open ${c.title}`}
            >
              <CourseCover src={c.cover_image} alt={c.title} icon={c.icon} />
              <div className="cc-header-row">
                <CourseIcon icon={c.icon} />
                <div className="cc-title">{c.title}</div>
              </div>
              <div className="cc-for">For: {c.target_audience}</div>
              <ul className="cc-list">
                {(c.learn_highlights || []).map((l, j) => <li key={j}>{l}</li>)}
              </ul>
              <div style={{ fontSize: '12px', color: 'var(--text-2)', padding: '10px 0', borderBottom: '1px solid var(--border)', fontStyle: 'italic' }}>
                Outcome: {c.outcome_summary}
              </div>
              <div className="cc-footer">
                <div className="cc-price"><small>₹</small>{c.price}</div>
                <span style={{ fontSize: '12px', color: 'var(--accent)', fontFamily: 'var(--fm)' }}>✓ Subscribed</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <h2 style={{ fontFamily: 'var(--fd)', fontSize: '24px', fontWeight: '700', color: 'var(--text)', margin: '40px 0 24px' }}>
        Subscribed Tools
      </h2>

      {subscribedTools.length === 0 ? (
        <div className="db-empty">
          <div className="db-empty-icon">🛠️</div>
          <h3>No tools yet</h3>
          <p>Subscribe to a tool to access it right here.</p>
          <button className="db-btn-primary" onClick={onBrowseTools}>Browse Tools</button>
        </div>
      ) : (
        <div className="course-grid">
          {subscribedTools.map((t) => {
            const toolPath = `/tools/${t.tool_id}`;
            const clickable = t.active
              ? { role: 'link', tabIndex: 0, onClick: () => navigate(toolPath), onKeyDown: openKey(toolPath), title: `Open ${t.name}` }
              : {};
            return (
            <div key={t.tool_id} className="cc" style={{ cursor: t.active ? 'pointer' : 'default', borderColor: t.active ? 'var(--accent)' : 'var(--border)' }} {...clickable}>
              <CourseCover src={t.cover_image} alt={t.name} icon={t.icon} />
              <div className="cc-header-row">
                <CourseIcon icon={t.icon} />
                <div className="cc-title">{t.name}</div>
              </div>
              {t.tagline && <div className="cc-for">{t.tagline}</div>}
              {(t.features || []).length > 0 && (
                <ul className="cc-list">
                  {t.features.map((f, j) => <li key={j}>{f}</li>)}
                </ul>
              )}
              {t.description && (
                <div style={{ fontSize: '12px', color: 'var(--text-2)', padding: '10px 0', borderBottom: '1px solid var(--border)', fontStyle: 'italic' }}>
                  {t.description}
                </div>
              )}
              <div className="cc-footer">
                <div style={{ fontSize: '12px', color: t.active ? 'var(--accent)' : 'var(--text-2)', fontFamily: 'var(--fm)' }}>
                  {t.active
                    ? (t.days_left != null ? `✓ ${t.days_left} day${t.days_left === 1 ? '' : 's'} left` : '✓ Active')
                    : 'Expired'}
                </div>
                {!t.active && <button className="btn-enr-s" onClick={(e) => { e.stopPropagation(); onBrowseTools(); }}>Renew</button>}
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── All Courses tab ───────────────────────────────────────────────────────────
function AllCourses({ courses, subscribedIds }) {
  const { token, refreshUser } = useAuth();
  const [payingId, setPayingId] = useState(null);

  const handleSubscribe = useCallback(async (course) => {
    if (!token) return;
    setPayingId(course.course_id);
    try {
      await loadRazorpayScript();
      const res = await createPaymentOrder(token, course.course_id);
      if (!res.data?.success || !res.data?.data) throw new Error(res.data?.message || 'Could not create order');
      const d = res.data.data;

      // Dev bypass — skip Razorpay modal, call verify directly
      if (d.bypass) {
        await verifyPayment(token, { bypass: true, payment_type: d.payment_type, payment_type_id: d.payment_type_id });
        await refreshUser();
        return;
      }

      const rzp = new window.Razorpay({
        key: d.keyId, amount: String(d.amount), currency: d.currency,
        name: d.name || 'InvFly', description: d.description || course.title,
        order_id: d.orderId, prefill: d.prefill || {}, theme: { color: '#00D4AA' },
        handler: async (r) => {
          try {
            await verifyPayment(token, {
              razorpay_payment_id: r.razorpay_payment_id,
              razorpay_order_id: r.razorpay_order_id,
              razorpay_signature: r.razorpay_signature,
            });
            await refreshUser();
          } catch (err) { window.alert(err.response?.data?.message || 'Could not verify payment'); }
        },
      });
      rzp.on('payment.failed', (r) => window.alert(r.error?.description || 'Payment failed'));
      rzp.open();
    } catch (err) {
      window.alert(err.response?.data?.message || err.message || 'Could not initiate payment');
    } finally { setPayingId(null); }
  }, [token, refreshUser]);

  if (courses.length === 0) {
    return (
      <div className="db-empty">
        <div className="db-empty-icon">🔍</div>
        <h3>No courses available</h3>
        <p>Check back soon for new courses.</p>
      </div>
    );
  }

  return (
    <div className="course-grid" style={{ paddingTop: '20px' }}>
      {courses.map((c) => {
        const enrolled = subscribedIds.has(c.course_id);
        const paying   = payingId === c.course_id;
        return (
          <div key={c.course_id} className="cc" style={{ cursor: 'default', borderColor: enrolled ? 'var(--accent)' : undefined }}>
            <CourseCover src={c.cover_image} alt={c.title} icon={c.icon} />
            <div className="cc-header-row">
              <CourseIcon icon={c.icon} />
              <div className="cc-title">{c.title}</div>
            </div>
            <div className="cc-for">For: {c.target_audience}</div>
            <ul className="cc-list">
              {(c.learn_highlights || []).map((l, j) => <li key={j}>{l}</li>)}
            </ul>
            <div style={{ fontSize: '12px', color: 'var(--text-2)', padding: '10px 0', borderBottom: '1px solid var(--border)', fontStyle: 'italic' }}>
              Outcome: {c.outcome_summary}
            </div>
            <div className="cc-footer">
              <div className="cc-price"><small>₹</small>{c.price}</div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <Link to={`/courses/${c.course_id}`} className="btn-enr-s">Details</Link>
                {enrolled ? (
                  <span style={{ fontSize: '12px', color: 'var(--accent)', fontFamily: 'var(--fm)' }}>✓ Subscribed</span>
                ) : (
                  c.is_enrollable ? (
                    <button className="btn-enr" disabled={paying} onClick={() => handleSubscribe(c)}>
                      {paying ? '…' : 'Subscribe'}
                    </button>
                  ) : (
                    <Link to="/contact" className="btn-enr-s">Enquire</Link>
                  )
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Dashboard Tools tab ───────────────────────────────────────────────────────
function DashboardTools({ catalogue = [] }) {
  const { token, refreshUser, paperTradingAccess } = useAuth();
  const navigate  = useNavigate();
  const [paying, setPaying] = useState(false);
  const [msg,    setMsg]    = useState('');

  const handleSubscribePaperTrading = useCallback(async () => {
    if (!token) return;
    setPaying(true); setMsg('');
    try {
      await loadRazorpayScript();
      const res = await createToolPaymentOrder(token, 'papertrading');
      if (!res.data?.success) throw new Error(res.data?.message || 'Could not create order');
      const d = res.data.data;

      // Dev bypass — skip Razorpay modal, call verify directly
      if (d.bypass) {
        await verifyToolPayment(token, { bypass: true, payment_type: d.payment_type, payment_type_id: d.payment_type_id });
        await refreshUser();
        setMsg('Access granted! Click "Open Tool" to start trading.');
        return;
      }

      const rzp = new window.Razorpay({
        key: d.keyId, amount: String(d.amount), currency: d.currency,
        name: d.name, description: d.description,
        order_id: d.orderId, prefill: d.prefill || {}, theme: { color: '#00D4AA' },
        handler: async (r) => {
          try {
            await verifyToolPayment(token, {
              razorpay_payment_id: r.razorpay_payment_id,
              razorpay_order_id:   r.razorpay_order_id,
              razorpay_signature:  r.razorpay_signature,
            });
            await refreshUser();
            setMsg('Payment successful! Click "Open Tool" to start trading.');
          } catch (err) { setMsg(err.response?.data?.message || 'Could not verify payment.'); }
        },
      });
      rzp.on('payment.failed', (r) => setMsg(r.error?.description || 'Payment failed.'));
      rzp.open();
    } catch (err) {
      setMsg(err.response?.data?.message || err.message || 'Could not initiate payment.');
    } finally { setPaying(false); }
  }, [token, refreshUser]);

  // Free tools render as cards; the paid paper-trading tool has its own card
  // below with subscription handling.
  const freeTools  = catalogue.filter((t) => t.is_active && !t.is_paid);
  const paperTool  = catalogue.find((t) => t.tool_id === 'papertrading');
  const ptPrice    = toolPriceLabel(paperTool) || '\u20b9499';

  const ptActive   = paperTradingAccess?.active;
  const daysLeft   = paperTradingAccess?.days_left;

  return (
    <div className="course-grid" style={{ paddingTop: '20px' }}>

      {/* ── Free tools (from the API, so admin edits are reflected) ── */}
      {freeTools.map((t, i) => (
        <div key={i} className="cc" style={{ cursor: 'default' }}>
          <div className="cc-header-row">
            <div className="db-course-icon" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {TOOL_ICONS[t.tool_id] || FALLBACK_TOOL_ICON}
            </div>
            <div className="cc-title">{t.name}</div>
          </div>
          <div className="cc-for">
            {!t.is_coming_soon ? (
              <span style={{ color: 'var(--accent)', fontWeight: 'bold', fontSize: '12px' }}>Live</span>
            ) : (
              <span style={{ color: 'var(--text-2)', fontSize: '12px' }}>Coming Soon</span>
            )}
          </div>
          <div style={{ fontSize: '14px', color: 'var(--text-2)', padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
            {t.description}
          </div>
          <div className="cc-footer">
            <div className="cc-price">{toolPriceLabel(t)}</div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {/* No CTA for coming-soon tools: offering "Open Tool" on something
                  unreleased is the bug this replaces. */}
              {!t.is_coming_soon && (
                TOOL_ROUTES[t.tool_id]
                  ? <button className="btn-enr-s" onClick={() => navigate(TOOL_ROUTES[t.tool_id])}>Open Tool</button>
                  : <button className="btn-enr-s">Open Tool</button>
              )}
            </div>
          </div>
        </div>
      ))}

      {/* ── Paper Trading (paid) ── */}
      <div className="cc" style={{ cursor: 'default', borderColor: ptActive ? 'var(--accent)' : undefined }}>
        <div className="cc-header-row">
          <div className="db-course-icon" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#00D4AA' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/>
              <polyline points="16 7 22 7 22 13"/>
            </svg>
          </div>
          <div className="cc-title">Paper Trading</div>
        </div>
        <div className="cc-for">
          <span style={{ background: '#00D4AA22', color: '#00D4AA', fontSize: '11px', fontWeight: '700', padding: '2px 8px', borderRadius: '20px' }}>PREMIUM</span>
          {ptActive && daysLeft !== undefined && (
            <span style={{ fontSize: '11px', color: 'var(--text-2)', marginLeft: '8px' }}>
              {daysLeft}d remaining
            </span>
          )}
        </div>
        <div style={{ fontSize: '14px', color: 'var(--text-2)', padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
          {paperTool?.description
            || 'Simulate real NSE trades with \u20b910,00,000 virtual capital. Live prices, full order book, positions & P&L tracking.'}
        </div>
        <div className="cc-footer">
          <div className="cc-price">{ptPrice}<span style={{ fontSize: '12px', color: 'var(--text-2)', fontWeight: '400' }}>/mo</span></div>
          <div style={{ display: 'flex', gap: '8px', flexDirection: 'column', alignItems: 'flex-end' }}>
            {ptActive ? (
              <button className="btn-enr" onClick={() => navigate('/tools/papertrading')}>
                Open Tool →
              </button>
            ) : (
              <button className="btn-enr" disabled={paying} onClick={handleSubscribePaperTrading}>
                {paying ? '…' : `Subscribe ${ptPrice}`}
              </button>
            )}
            {msg && <div style={{ fontSize: '11px', color: 'var(--accent)', textAlign: 'right', maxWidth: '140px' }}>{msg}</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Top Bar ───────────────────────────────────────────────────────────────────
function DashboardTopBar({ tab, setTab, subscribedCount, onOpenProfile, theme, setTheme }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const displayName = user?.name?.trim() || user?.email?.split('@')[0] || 'Account';
  const userInitial = (user?.name?.[0] || user?.email?.[0] || 'U').toUpperCase();

  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    if (menuOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <header className="db-topbar">
      <div className="db-topbar-inner">
      {/* Logo */}
      <Link to="/" className="db-topbar-logo">
        <img src={logo} alt="InvFly" style={{ height: 40, width: 'auto', objectFit: 'contain' }} />
      </Link>

      {/* Centre tabs */}
      <nav className="db-topbar-tabs" role="tablist">
        <button
          role="tab"
          aria-selected={tab === 'dashboard'}
          className={`db-topbar-tab${tab === 'dashboard' ? ' active' : ''}`}
          onClick={() => setTab('dashboard')}
        >
          Dashboard
        </button>
        <button
          role="tab"
          aria-selected={tab === 'courses'}
          className={`db-topbar-tab${tab === 'courses' ? ' active' : ''}`}
          onClick={() => setTab('courses')}
        >
          Courses
        </button>
        <button
          role="tab"
          aria-selected={tab === 'tools'}
          className={`db-topbar-tab${tab === 'tools' ? ' active' : ''}`}
          onClick={() => setTab('tools')}
        >
          Tools
        </button>
      </nav>

      {/* Right — theme toggle + user menu */}
      <div className="db-topbar-right">
      {setTheme && (
        <button
          className="db-theme-toggle"
          onClick={() => setTheme((t) => (t === 'dark' ? 'light' : 'dark'))}
          title="Toggle theme"
          aria-label="Toggle theme"
        >
          {theme === 'dark' ? <DbSunIcon /> : <DbMoonIcon />}
        </button>
      )}
      <div className="db-topbar-user" ref={menuRef}>
        <button
          className="db-user-btn"
          onClick={() => setMenuOpen((o) => !o)}
          aria-haspopup="true"
          aria-expanded={menuOpen}
        >
          <span className="db-avatar-sm">{userInitial}</span>
          <span className="db-user-name">{displayName}</span>
          <svg className={`db-chevron${menuOpen ? ' open' : ''}`} width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        {menuOpen && (
          <div className="db-user-dropdown" role="menu">
            <div className="db-user-dropdown-header">
              <span className="db-avatar-sm">{userInitial}</span>
              <div>
                <div className="db-dropdown-name">{displayName}</div>
                {user?.email && <div className="db-dropdown-email">{user.email}</div>}
              </div>
            </div>
            <div className="db-user-dropdown-divider" />
            <button
              role="menuitem"
              className="db-dropdown-item"
              onClick={() => { setMenuOpen(false); onOpenProfile(); }}
            >
              <span className="db-dropdown-icon">👤</span> View Profile
            </button>
            <div className="db-user-dropdown-divider" />
            <button
              role="menuitem"
              className="db-dropdown-item db-dropdown-item--danger"
              onClick={handleLogout}
            >
              <span className="db-dropdown-icon">↩</span> Log out
            </button>
          </div>
        )}
      </div>
      </div>
      </div>
    </header>
  );
}

// ── Dashboard Page ────────────────────────────────────────────────────────────
const UserDashboard = ({ theme, setTheme }) => {
  const { user } = useAuth();
  const [tab, setTab] = useState('dashboard');
  const [profileOpen, setProfileOpen] = useState(false);
  const [courses, setCourses] = useState([]);
  const [tools, setTools] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setCoursesLoading(true);
    Promise.allSettled([listCourses(), listTools()])
      .then(([cRes, tRes]) => {
        if (cancelled) return;
        if (cRes.status === 'fulfilled' && cRes.value.data?.success) setCourses(cRes.value.data.data || []);
        if (tRes.status === 'fulfilled' && tRes.value.data?.success) setTools(tRes.value.data.data || []);
      })
      .finally(() => { if (!cancelled) setCoursesLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const subscribedIds = new Set((user?.subscribed_courses || []).map((s) => s.course_id));

  // Cross-reference the tools catalog with the user's subscriptions (latest
  // expiry per tool) so the dashboard can show subscribed tools like courses.
  const now = Date.now();
  const toolExpiry = {};
  (user?.subscribed_tools || []).forEach((t) => {
    const exp = t.expires_at ? new Date(t.expires_at).getTime() : Infinity;
    if (toolExpiry[t.tool_id] === undefined || exp > toolExpiry[t.tool_id]) toolExpiry[t.tool_id] = exp;
  });
  const subscribedTools = tools
    .filter((t) => toolExpiry[t.tool_id] !== undefined)
    .map((t) => {
      const exp = toolExpiry[t.tool_id];
      const active = exp === Infinity || exp > now;
      const daysLeft = exp === Infinity ? null : Math.max(0, Math.ceil((exp - now) / 86400000));
      return { ...t, active, days_left: daysLeft };
    });

  return (
    <div className="db-page">
      <Seo title="Dashboard — InvFly" description="Your InvFly learning dashboard." noIndex />

      <DashboardTopBar
        tab={tab}
        setTab={setTab}
        subscribedCount={subscribedIds.size}
        onOpenProfile={() => setProfileOpen(true)}
        theme={theme}
        setTheme={setTheme}
      />

      <div className="db-content">
        {coursesLoading ? (
          <div className="db-empty">
            <div style={{ color: 'var(--text-2)', fontSize: 15 }}>Loading courses…</div>
          </div>
        ) : (
          <>
            {tab === 'dashboard' && (
              <DashboardTab
                courses={courses}
                subscribedIds={subscribedIds}
                subscribedTools={subscribedTools}
                onBrowse={() => setTab('courses')}
                onBrowseTools={() => setTab('tools')}
              />
            )}
            {tab === 'courses' && (
              <AllCourses courses={courses} subscribedIds={subscribedIds} />
            )}
            {tab === 'tools' && (
              <DashboardTools catalogue={tools} />
            )}
          </>
        )}
      </div>

      {profileOpen && <ProfileModal onClose={() => setProfileOpen(false)} />}
    </div>
  );
};

export default UserDashboard;
